import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

/**
 * Privacy Policy Page for Spawnry
 * Comprehensive privacy policy covering all data collection, tracking, and user rights
 */
const PrivacyPolicy = () => {
  const sectionStyle = {
    marginBottom: '2rem'
  };

  const headingStyle = {
    marginBottom: '1rem',
    color: '#1a1a2e',
    fontWeight: '600'
  };

  const subheadingStyle = {
    fontSize: '1.1rem',
    marginTop: '1.5rem',
    color: '#1a1a2e',
    fontWeight: '600'
  };

  return (
    <>
      <Helmet>
        <title>Privacy Policy | Spawnry</title>
        <meta name="description" content="Privacy Policy for Spawnry - how we collect, use, and protect your data" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* Dark Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        padding: '100px 0 4rem'
      }}>
        <Container>
          <Row className="justify-content-center">
            <Col md={10} lg={8} className="text-center">
              <h1 style={{
                color: 'white',
                fontWeight: '700',
                fontSize: 'clamp(1.75rem, 3vw, 2.25rem)',
                marginBottom: '0.75rem'
              }}>
                Privacy Policy
              </h1>
              <p style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '1.1rem',
                margin: 0
              }}>
                Effective Date: January 24, 2026
              </p>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Content Card */}
      <Container style={{ marginTop: '-2rem', paddingBottom: '3rem' }}>
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '2.5rem',
              boxShadow: '0 4px 24px rgba(0,0,0,0.08)'
            }}>
              <section style={sectionStyle}>
                <h2 style={headingStyle}>1. Introduction</h2>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>
                  This Privacy Policy describes how Spawnry, a service of Teq Crew, LLC ("we", "us", "our"),
                  collects, uses, shares, and protects information when you use spawnry.ai and its
                  services (collectively, the "Services").
                </p>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>
                  We are committed to protecting your privacy and being transparent about our data
                  practices. By using our Services, you agree to the collection and use of information
                  in accordance with this Privacy Policy. This policy should be read in conjunction
                  with our <Link to="/terms-of-service" style={{ color: '#6366f1', textDecoration: 'none', fontWeight: '500' }}>Terms of Service</Link>.
                </p>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>
                  If you have any concerns about our data practices, please reach out through the{' '}
                  <Link to="/contact" style={{ color: '#6366f1', textDecoration: 'none', fontWeight: '500' }}>contact form</Link>.
                </p>
              </section>

              <section style={sectionStyle}>
                <h2 style={headingStyle}>2. Information We Collect</h2>

                <h3 style={subheadingStyle}>2.1 Information You Provide Directly</h3>
                <ul style={{ color: '#64748b', lineHeight: '1.9' }}>
                  <li><strong style={{ color: '#1a1a2e' }}>Email Address:</strong> When you use the Article to Media tool (required for processing)</li>
                  <li><strong style={{ color: '#1a1a2e' }}>Article URLs:</strong> When you use our Article to Media tool</li>
                  <li><strong style={{ color: '#1a1a2e' }}>Access Codes:</strong> If you use an access code for enhanced features</li>
                  <li><strong style={{ color: '#1a1a2e' }}>Contact Information:</strong> Name, email, and message content when you use our contact form</li>
                  <li><strong style={{ color: '#1a1a2e' }}>Feedback:</strong> When you report problems or provide feedback on generated content</li>
                </ul>

                <h3 style={subheadingStyle}>2.2 Information Collected Automatically</h3>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>When you visit our website, we automatically collect certain information:</p>
                <ul style={{ color: '#64748b', lineHeight: '1.9' }}>
                  <li><strong style={{ color: '#1a1a2e' }}>Device Information:</strong> Browser type, operating system, device type, screen resolution</li>
                  <li><strong style={{ color: '#1a1a2e' }}>Usage Data:</strong> Pages visited, time spent on pages, click patterns, scroll depth</li>
                  <li><strong style={{ color: '#1a1a2e' }}>IP Address:</strong> We may collect and hash IP addresses for security and rate limiting (we do not store full IP addresses long-term)</li>
                  <li><strong style={{ color: '#1a1a2e' }}>Referral Data:</strong> How you arrived at our website</li>
                  <li><strong style={{ color: '#1a1a2e' }}>Session Information:</strong> Session identifiers to manage your experience during a visit</li>
                </ul>

                <h3 style={subheadingStyle}>2.3 Information from Third-Party Analytics</h3>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>We may use third-party analytics services that collect information about your use of our website:</p>
                <ul style={{ color: '#64748b', lineHeight: '1.9' }}>
                  <li><strong style={{ color: '#1a1a2e' }}>Google Analytics:</strong> Collects anonymized usage data including pages visited, session duration, geographic region, and traffic sources</li>
                  <li><strong style={{ color: '#1a1a2e' }}>Google Tag Manager:</strong> Manages and deploys tracking tags on our website</li>
                </ul>
              </section>

              <section style={sectionStyle}>
                <h2 style={headingStyle}>3. How We Use Your Information</h2>

                <h3 style={subheadingStyle}>3.1 Providing Services</h3>
                <ul style={{ color: '#64748b', lineHeight: '1.9' }}>
                  <li>Process your requests (e.g., converting articles to media)</li>
                  <li>Communicate about domain approval status for pending requests</li>
                  <li>Respond to your inquiries and support requests</li>
                  <li>Manage access codes and usage limits</li>
                </ul>

                <h3 style={subheadingStyle}>3.2 Improving Our Services</h3>
                <ul style={{ color: '#64748b', lineHeight: '1.9' }}>
                  <li>Analyze usage patterns to improve user experience</li>
                  <li>Identify and fix bugs and technical issues</li>
                  <li>Develop new features and tools</li>
                </ul>

                <h3 style={subheadingStyle}>3.3 Security and Operations</h3>
                <ul style={{ color: '#64748b', lineHeight: '1.9' }}>
                  <li>Prevent abuse, fraud, and unauthorized access</li>
                  <li>Enforce usage limits and rate limiting</li>
                  <li>Monitor for security threats</li>
                </ul>
              </section>

              <section style={sectionStyle}>
                <h2 style={headingStyle}>4. Cookies and Tracking Technologies</h2>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>We use cookies and similar tracking technologies:</p>

                <h3 style={subheadingStyle}>4.1 Essential Cookies</h3>
                <ul style={{ color: '#64748b', lineHeight: '1.9' }}>
                  <li>Session management</li>
                  <li>CAPTCHA verification</li>
                  <li>Security and authentication</li>
                </ul>

                <h3 style={subheadingStyle}>4.2 Analytics Cookies</h3>
                <ul style={{ color: '#64748b', lineHeight: '1.9' }}>
                  <li>Google Analytics cookies (_ga, _gid, _gat)</li>
                </ul>

                <h3 style={subheadingStyle}>4.3 Managing Cookies</h3>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>
                  Most web browsers allow you to control cookies through their settings.
                  You can opt out of Google Analytics by installing the{' '}
                  <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', textDecoration: 'none', fontWeight: '500' }}>
                    Google Analytics Opt-out Browser Add-on
                  </a>.
                </p>
              </section>

              <section style={sectionStyle}>
                <h2 style={headingStyle}>5. Data Storage and Retention</h2>

                <h3 style={subheadingStyle}>5.1 Generated Content</h3>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>
                  Generated content (audio files, images, text summaries) is stored on our servers
                  and accessible via unique URLs. Content is stored indefinitely unless you request
                  deletion or we determine removal is necessary.
                </p>

                <h3 style={subheadingStyle}>5.2 Email Addresses</h3>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>
                  We retain your email address to communicate about domain approvals and service updates.
                  You can request deletion at any time.
                </p>
              </section>

              <section style={sectionStyle}>
                <h2 style={headingStyle}>6. Data Sharing and Disclosure</h2>
                <div style={{ background: 'rgba(16, 185, 129, 0.1)', padding: '1rem', borderRadius: '8px', border: '1px solid rgba(16, 185, 129, 0.2)', marginBottom: '1rem' }}>
                  <p style={{ color: '#059669', fontWeight: '600', marginBottom: 0 }}>We do not sell your personal information.</p>
                </div>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>We may share data with:</p>

                <h3 style={subheadingStyle}>6.1 Service Providers</h3>
                <ul style={{ color: '#64748b', lineHeight: '1.9' }}>
                  <li><strong style={{ color: '#1a1a2e' }}>OpenAI:</strong> Processes content for AI-powered features</li>
                  <li><strong style={{ color: '#1a1a2e' }}>Amazon Web Services (AWS):</strong> Hosts our infrastructure</li>
                  <li><strong style={{ color: '#1a1a2e' }}>SendGrid:</strong> Email delivery services</li>
                  <li><strong style={{ color: '#1a1a2e' }}>Google Analytics:</strong> Analytics services</li>
                </ul>

                <h3 style={subheadingStyle}>6.2 Legal Requirements</h3>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>We may disclose information if required to comply with applicable laws or legal processes.</p>
              </section>

              <section style={sectionStyle}>
                <h2 style={headingStyle}>7. Your Rights</h2>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>You may have the following rights:</p>
                <ul style={{ color: '#64748b', lineHeight: '1.9' }}>
                  <li><strong style={{ color: '#1a1a2e' }}>Access:</strong> Request information about what personal data we have collected</li>
                  <li><strong style={{ color: '#1a1a2e' }}>Correction:</strong> Request correction of inaccurate personal data</li>
                  <li><strong style={{ color: '#1a1a2e' }}>Deletion:</strong> Request deletion of your personal data</li>
                </ul>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>
                  To exercise these rights, please contact us through the{' '}
                  <Link to="/contact" style={{ color: '#6366f1', textDecoration: 'none', fontWeight: '500' }}>contact form</Link>.
                </p>
              </section>

              <section style={sectionStyle}>
                <h2 style={headingStyle}>8. Data Security</h2>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>We implement reasonable security measures including:</p>
                <ul style={{ color: '#64748b', lineHeight: '1.9' }}>
                  <li>Encryption in transit (HTTPS/TLS)</li>
                  <li>Secure cloud infrastructure with access controls</li>
                  <li>Limited access to personal data</li>
                </ul>
              </section>

              <section style={sectionStyle}>
                <h2 style={headingStyle}>9. Children's Privacy</h2>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>
                  Our Services are not directed to children under 13. We do not knowingly collect
                  personal information from children.
                </p>
              </section>

              <section style={sectionStyle}>
                <h2 style={headingStyle}>10. Changes to This Policy</h2>
                <p style={{ color: '#64748b', lineHeight: '1.7' }}>
                  We may update this Privacy Policy from time to time. Changes will be posted on
                  this page with an updated "Effective Date."
                </p>
              </section>

              <section style={{ marginBottom: '1rem' }}>
                <h2 style={headingStyle}>11. Contact</h2>
                <p style={{ color: '#64748b', lineHeight: '1.7', marginBottom: 0 }}>
                  If you have questions about this Privacy Policy, please reach out through the{' '}
                  <Link to="/contact" style={{ color: '#6366f1', textDecoration: 'none', fontWeight: '500' }}>contact form</Link>.
                </p>
              </section>

            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default PrivacyPolicy;
